// src/views/FileManager/constants.ts
import { FileAction } from '../../enums/fileActions';
import { UserRole } from '../../enums/userEnums';
import { AllowedRoles } from '../../routes/routeConstants/allowedRoles';
import { AccessRequirements } from '../../shared/utils/accessControl';

// Role gate per action, from the File Manager RBAC matrix. Ids left out are open to
// every role -- checkAccess reads an undefined allowedRoles as no restriction.
export const FILE_ACTION_ALLOWED_ROLES: Partial<Record<FileAction, UserRole[]>> = {
  [FileAction.RENAME_FILE]: AllowedRoles.EDITOR_AND_ABOVE,
  [FileAction.MOVE_FILE]: AllowedRoles.EDITOR_AND_ABOVE,
  [FileAction.MOVE_ALL]: AllowedRoles.EDITOR_AND_ABOVE,
  [FileAction.DELETE_FILES]: AllowedRoles.EDITOR_AND_ABOVE,
  [FileAction.DELETE_ALL]: AllowedRoles.EDITOR_AND_ABOVE,
  [FileAction.REUPLOAD_FILE]: AllowedRoles.EDITOR_AND_ABOVE,
  [FileAction.FILE_PERMISSIONS]: AllowedRoles.EDITOR_AND_ABOVE,
  [FileAction.RESTORE_FILE]: AllowedRoles.EDITOR_AND_ABOVE,
  [FileAction.RESTORE_ALL]: AllowedRoles.EDITOR_AND_ABOVE,
  [FileAction.PERMANENTLY_DELETE_FILE]: AllowedRoles.EDITOR_AND_ABOVE,
  [FileAction.PERMANENTLY_DELETE_ALL]: AllowedRoles.EDITOR_AND_ABOVE,
};

export const fileActionAccess = (actionId: string): AccessRequirements => ({
  allowedRoles: FILE_ACTION_ALLOWED_ROLES[actionId as FileAction],
});

/** Label for the top-level root, whose backend id and name are opaque. TODO: rename to "Home". */
export const ROOT_DISPLAY_NAME = 'Root';

/** Spatial extensions that open the temporary fullscreen map preview (US-109521). GeoPDF/PDF deferred. */
export const SPATIAL_PREVIEW_EXTS: ReadonlySet<string> = new Set([
  'shp',
  'dbf',
  'shx',
  'prj',
  'geojson',
  'json',
  'kml',
  'kmz',
  'gpkg',
  'gml',
  'tif',
  'tiff',
  'gpx',
  'gdb',
]);

/** Extensions with no standard or spatial preview available -- Open is disabled for these. */
export const NO_PREVIEW_EXTS: ReadonlySet<string> = new Set(['dxf', 'dwg']);

// File Type is chosen by the user before picking files (US-109511/US-109513),
// then enforces which extensions the upload step will accept.
export const uploadFileTypeOptions = [
  // Spatial
  'SHAPEFILE',
  'GEOJSON',
  'KML',
  'KMZ',
  'TIF',
  'TIFF',
  'DWG',
  'DXF',
  // Regular
  'PDF',
  'DOCX',
  'CSV',
  'XLSX',
  'PNG',
  'JPG',
  'MP4',
];

const fileTypeExtensions: Record<string, string[]> = {
  SHAPEFILE: ['shp', 'shx', 'dbf', 'prj'],
  GEOJSON: ['geojson'],
  KML: ['kml'],
  KMZ: ['kmz'],
  TIF: ['tif'],
  TIFF: ['tiff'],
  DWG: ['dwg'],
  DXF: ['dxf'],
  PDF: ['pdf'],
  DOCX: ['doc', 'docx'],
  CSV: ['csv'],
  XLSX: ['xls', 'xlsx'],
  PNG: ['png'],
  JPG: ['jpg', 'jpeg'],
  MP4: ['mp4'],
};

const extensionToFileType = new Map<string, string>(
  Object.entries(fileTypeExtensions).flatMap(([fileType, extensions]) => extensions.map((extension) => [extension, fileType] as const)),
);

// Accepts "shp", ".shp" or "map.shp".
const normalizeExtension = (value: string): string => value.slice(value.lastIndexOf('.') + 1).toLowerCase();

export const getExtensionsForFileType = (fileType: string): string[] => fileTypeExtensions[fileType] ?? [];

// Sibling extensions that share the same upload File Type (e.g. jpg/jpeg), so
// reupload accepts any equivalent extension rather than only the exact original.
export const getExtensionGroup = (ext: string): string[] => {
  const normalized = normalizeExtension(ext);
  const fileType = extensionToFileType.get(normalized);
  return fileType ? fileTypeExtensions[fileType] : [normalized];
};

// The generic upload gate in useFileUpload.ts (drag-and-drop bypasses the File
// Type picker above, so it's the only check that path gets) -- always includes
// every fileTypeExtensions entry so a new upload File Type can't silently fail
// it again, plus formats that aren't offered as a File Type at all.
export const allowedExtensions: ReadonlySet<string> = new Set([
  ...Object.values(fileTypeExtensions).flat(),
  'dbx',
  'svg',
  'tifw',
  'tfw',
  'tfwx',
  'mov',
  'avi',
  'wmv',
  'url',
  'zip',
  'rar',
  '7z',
  'gz',
  'cpg',
  'sbn',
  'sbx',
  'qix',
  'lyr',
  'xml',
  'tar',
  'gif',
  'apng',
  'webp',
  'avif',
  'webm',
  'ogg',
  'mp3',
  'wav',
  'aac',
]);

// Per-type max upload size (US-109511 Scenario 4). Vector spatial types share a
// flat 200 MiB cap; only rasters (TIF/TIFF) need the larger 5 GiB allowance.
// All limits are binary (2**20 / 2**30), so a "200.2 MB" or "5.2 GB" file as
// reported by the OS still fits under the nominal 200 MB / 5 GB label.
const fileTypeMaxBytes: Record<string, number> = {
  SHAPEFILE: 200 * 2 ** 20,
  GEOJSON: 200 * 2 ** 20,
  KML: 200 * 2 ** 20,
  KMZ: 200 * 2 ** 20,
  TIF: 5 * 2 ** 30,
  TIFF: 5 * 2 ** 30,
  DWG: 200 * 2 ** 20,
  DXF: 200 * 2 ** 20,
  PDF: 200 * 2 ** 20,
  DOCX: 200 * 2 ** 20,
  CSV: 5 * 2 ** 20,
  XLSX: 5 * 2 ** 20,
  PNG: 15 * 2 ** 20,
  JPG: 15 * 2 ** 20,
  MP4: 500 * 2 ** 20,
};

export const getMaxBytesForFileType = (fileType: string): number => fileTypeMaxBytes[fileType] ?? 2 * 2 ** 30;

export const getMaxBytesForExtension = (extension: string): number => {
  const fileType = extensionToFileType.get(normalizeExtension(extension));
  return fileType ? getMaxBytesForFileType(fileType) : 2 * 2 ** 30;
};

export const formatMaxSize = (bytes: number): string => (bytes >= 2 ** 30 ? `${bytes / 2 ** 30} GB` : `${bytes / 2 ** 20} MB`);

const SHAPEFILE_CUMULATIVE_LIMIT = getMaxBytesForFileType('SHAPEFILE');

// Shapefile parts share one cap, so this only reports once the complete set is
// present; an incomplete selection is still in progress and returns null.
export const validateShapefileCumulativeSize = (
  files: ReadonlyArray<{ name: string; size?: number }>,
  requiredExtensions: readonly string[],
): string | null => {
  const present = new Set(files.map((file) => normalizeExtension(file.name)));
  if (!requiredExtensions.every((ext) => present.has(normalizeExtension(ext)))) {
    return null;
  }

  const cumulativeSize = files.reduce((total, file) => total + (file.size ?? 0), 0);
  return cumulativeSize > SHAPEFILE_CUMULATIVE_LIMIT
    ? `The shapefile files exceed the cumulative ${formatMaxSize(SHAPEFILE_CUMULATIVE_LIMIT)} limit.`
    : null;
};

// Category -> extensions lookup, keyed to match FileTypes enum values
// (src/enums/fileTypes.ts) for the layer-upload "source format" dropdowns.
export const fileTypeMap: Record<string, string[]> = {
  Document: ['.pdf', '.doc', '.docx', '.txt', '.odt', '.ods'],
  Image: ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp'],
  Video: ['.mp4', '.avi', '.mov', '.mkv'],
  Audio: ['.mp3', '.wav', '.aac', '.ogg', '.flac'],
  Spreadsheet: ['.csv', '.xls', '.xlsx', '.ods'],
  GeoJSON: ['.geojson'],
  Shapefile: ['.shp', '.dbf', '.shx', '.prj'],
  Geodatabase: ['.gdb'],
  Raster: ['.tif', '.tiff'],
  Compressed: ['.zip', '.rar', '.7z', '.tar.gz', '.bz2'],
  'Web Map Service': ['.wms'],
  Other: ['*'],
  Kml: ['.kml', '.kmz'],
  Kmz: ['.kmz'],
};

// Human-readable byte size for display (e.g. "1.5 MB"). Returns "" for a
// missing size (folders carry none), so the caller can leave the cell blank.
export const formatBytes = (bytes?: number | null, decimals = 2): string => {
  if (bytes === null || bytes === undefined) return '';
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
};
